// Construction-type, roof-type, foundation-type and wall-finish options + their
// cost multipliers. The multipliers are applied on top of the existing
// currency × library × finish chain in CategoryToggleTab and buildExtensionShellItems.
//
// Scope of application (per product spec):
//   - Extensions shell items: Foundation, Structural frame, Internal lining → construction_mult
//                             Foundation                                 → × foundation_mult
//                             Roof                                       → × roof_mult
//                             Exterior cladding                          → × wall_finish_mult
//   - Exterior categories:    'roof'           → × roof_mult
//                             'boundary_walls' → × construction_mult
//                             'exterior_paint' → × wall_finish_mult (cladding/face-brick are pricier to paint)
//
// All other categories and the entire interior pipeline are intentionally untouched.

export const CONSTRUCTION_TYPES = [
  { value: "wood",     label: "Wood frame",      sub: "Timber stud — common in USA, Canada, AUS, NZ",   mult: 1.00 },
  { value: "brick",    label: "Brick & mortar",  sub: "Solid masonry — common in ZA, UK, Europe",       mult: 1.25 },
  { value: "steel",    label: "Steel frame",     sub: "Light gauge or structural steel",                mult: 1.30 },
  { value: "concrete", label: "Concrete",        sub: "Precast or cast in place — premium structural",  mult: 1.40 },
];

export const ROOF_TYPES = [
  { value: "shingle", label: "Asphalt shingle",  sub: "Common in North America",                        mult: 1.00 },
  { value: "metal",   label: "Metal / Colorbond", sub: "Sheet steel or aluminium",                       mult: 1.15 },
  { value: "flat",    label: "Flat membrane",     sub: "Bitumen / TPO / EPDM",                           mult: 1.10 },
  { value: "tile",    label: "Concrete or clay tile", sub: "Common in ZA, EU, AUS",                      mult: 1.40 },
  { value: "thatch",  label: "Thatch",            sub: "Specialist labour and maintenance",              mult: 1.50 },
  { value: "slate",   label: "Slate",             sub: "Premium natural stone",                          mult: 2.00 },
];

export const FOUNDATION_TYPES = [
  { value: "slab",     label: "Slab on grade",  sub: "Concrete slab directly on prepared sub-base",      mult: 1.00 },
  { value: "raised",   label: "Raised / pier",  sub: "Suspended floor over crawl-space",                 mult: 1.20 },
  { value: "basement", label: "Basement",       sub: "Excavated below grade — adds significant cost",    mult: 1.80 },
];

export const WALL_FINISHES = [
  { value: "plaster",    label: "Plaster & paint", sub: "Smooth plaster or render with paint",         mult: 1.00 },
  { value: "face_brick", label: "Face brick",      sub: "Visible brick — no plaster",                  mult: 1.15 },
  { value: "cladding",   label: "Cladding",        sub: "Timber, fibre-cement or composite panels",     mult: 1.25 },
];

const _byVal = (arr) => Object.fromEntries(arr.map((it) => [it.value, it]));
const C = _byVal(CONSTRUCTION_TYPES);
const R = _byVal(ROOF_TYPES);
const F = _byVal(FOUNDATION_TYPES);
const W = _byVal(WALL_FINISHES);

function pick(map, key) {
  return map[key]?.mult ?? 1.0;
}

// Multiplier for a given extension shell item (looked up by item NAME).
// Returns 1.0 for items the construction settings don't influence.
export function extensionShellMultiplier(itemName, project) {
  if (!itemName) return 1.0;
  const construction = pick(C, project?.construction_type);
  const roof         = pick(R, project?.roof_type);
  const foundation   = pick(F, project?.foundation_type);
  const wall         = pick(W, project?.wall_finish);

  const n = String(itemName).toLowerCase();
  if (n === "foundation")              return construction * foundation;
  if (n === "structural frame")        return construction;
  if (n === "internal lining")         return construction;
  if (n === "roof")                    return roof;
  if (n === "exterior cladding")       return wall;
  return 1.0;
}

// Multiplier for an Exterior category KEY (used by CategoryToggleTab.ensureItems).
// Systems uses the same component, but we explicitly skip systems by checking
// `field === 'exterior'` at the call site so this only affects Exterior.
export function exteriorCategoryMultiplier(categoryKey, project) {
  if (!categoryKey) return 1.0;
  const construction = pick(C, project?.construction_type);
  const roof         = pick(R, project?.roof_type);
  const wall         = pick(W, project?.wall_finish);

  if (categoryKey === "roof")           return roof;
  if (categoryKey === "boundary_walls") return construction;
  if (categoryKey === "exterior_paint") return wall;
  return 1.0;
}

// Helper for the Cost Library info banner.
export function constructionSummary(project) {
  if (!project) return null;
  const parts = [
    project.construction_type ? C[project.construction_type]?.label : null,
    project.roof_type ? `${R[project.roof_type]?.label} roof` : null,
    project.foundation_type ? `${F[project.foundation_type]?.label} foundation` : null,
    project.wall_finish ? W[project.wall_finish]?.label : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

// True if every dimension has been answered. Used to gate the "Continue"
// button in the New Project dialog.
export function isConstructionComplete(p) {
  return !!(p?.construction_type && p?.roof_type && p?.foundation_type && p?.wall_finish);
}
